import React from 'react';
import { Link } from 'react-router-dom';
import { aboutData, executionModelData, leadershipTeamData, managementTeamData } from '../data/mock';
import { Briefcase, GraduationCap, Award } from 'lucide-react';

const About = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h1 className="hero-large mb-6">{aboutData.title}</h1>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', margin: '0 auto' }}>
              {aboutData.description}
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="grid-two-column">
            <div>
              <img 
                src={aboutData.image} 
                alt="Pinnacle Biomed Facility"
                style={{ 
                  width: '100%', 
                  height: '500px', 
                  objectFit: 'cover',
                  borderRadius: '0px'
                }}
              />
            </div>
            <div>
              <h2 className="hero-medium mb-6">Our Mission</h2>
              <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
                {aboutData.mission}
              </p>
              <h3 className="heading-2 mb-4">Corporate Commitment</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {aboutData.commitment}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Turnkey Execution Model */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">Turnkey Project Execution Model</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Structured execution ensuring quality, compliance, and timely delivery across all healthcare projects
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {executionModelData.map((step) => (
              <div 
                key={step.step}
                style={{
                  background: 'var(--bg-secondary)',
                  padding: '32px',
                  borderRadius: '0px',
                  border: '1px solid var(--border-light)',
                  position: 'relative'
                }}
              >
                <div 
                  style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    width: '40px',
                    height: '40px',
                    background: 'var(--interactive-base)',
                    color: 'var(--bg-primary)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    fontWeight: '700',
                    fontFamily: "'Suisse Medium', sans-serif"
                  }}
                >
                  {step.step}
                </div>
                <h3 className="heading-2 mb-3 pr-12">{step.title}</h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Differentiators */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">What Sets Us Apart</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="heading-2 mb-4">Single-Agency Responsibility</h3>
              <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
                Unlike traditional multi-vendor approaches, we provide complete accountability from conceptualization through commissioning to long-term operations. One point of contact, seamless execution.
              </p>
            </div>

            <div>
              <h3 className="heading-2 mb-4">Decades of Proven Experience</h3>
              <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
                Our dedicated professional specialists bring decades of proven experience across all levels of hospital facilities, from primary health centers to 3600-bed post-graduate institutes.
              </p>
            </div>

            <div>
              <h3 className="heading-2 mb-4">Advanced Laboratory Capabilities</h3>
              <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
                Specialized infrastructure capability for NBS Laboratory, BSL-2, and BSL-3 laboratory setups, supporting advanced medical research and diagnostic facilities.
              </p>
            </div>

            <div>
              <h3 className="heading-2 mb-4">Comprehensive Compliance</h3>
              <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
                Full compliance with national and international healthcare standards including NMC, NABH, ICMR, HTM, NFPA, ISO, IPHS, NBC, and specialized transplant society requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: 'var(--brand-teal)' }}>Leadership</span>
              {' '}
              <span style={{ color: 'var(--brand-orange)' }}>Team</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Visionary leaders with decades of experience driving excellence in healthcare infrastructure and pharmaceutical services
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {leadershipTeamData.map((leader) => (
              <div 
                key={leader.id}
                className="leadership-card"
                style={{
                  background: 'var(--bg-secondary)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid var(--border-light)',
                  transition: 'all 0.3s ease'
                }}
              >
                {/* Leader Image */}
                <div style={{ position: 'relative' }}>
                  <img 
                    src={leader.image} 
                    alt={leader.name}
                    style={{ 
                      width: '100%', 
                      height: '300px', 
                      objectFit: 'cover',
                      objectPosition: 'top'
                    }}
                  />
                  <div 
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                      padding: '40px 24px 16px 24px'
                    }}
                  >
                    <h3 className="heading-1" style={{ color: 'white', marginBottom: '4px' }}>
                      {leader.name}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--brand-orange)', fontWeight: '600' }}>
                      {leader.title}
                    </p>
                  </div>
                </div>

                {/* Leader Details */}
                <div style={{ padding: '24px' }}>
                  <p className="body-regular mb-4" style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                    {leader.bio}
                  </p>

                  {/* Expertise */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Briefcase size={16} style={{ color: 'var(--brand-teal)' }} />
                      <span className="body-small" style={{ color: 'var(--brand-teal)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Expertise
                      </span>
                    </div>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      {leader.expertise}
                    </p>
                  </div>

                  {/* Ventures */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Briefcase size={16} style={{ color: 'var(--brand-orange)' }} />
                      <span className="body-small" style={{ color: 'var(--brand-orange)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Key Ventures
                      </span>
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '16px' }}>
                      {leader.ventures.slice(0, 3).map((venture, idx) => (
                        <li key={idx} className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
                          {venture}
                        </li>
                      ))}
                      {leader.ventures.length > 3 && (
                        <li className="body-small" style={{ color: 'var(--text-light)' }}>
                          +{leader.ventures.length - 3} more ventures
                        </li>
                      )}
                    </ul>
                  </div>

                  {/* Education */}
                  {leader.education && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <GraduationCap size={16} style={{ color: 'var(--brand-teal)' }} />
                        <span className="body-small" style={{ color: 'var(--brand-teal)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Education
                        </span>
                      </div>
                      <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        {leader.education}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Management Team */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: 'var(--brand-orange)' }}>Management</span>
              {' '}
              <span style={{ color: 'var(--brand-teal)' }}>Team</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Experienced professionals driving operational excellence in healthcare infrastructure projects
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {managementTeamData.map((member) => (
              <div 
                key={member.id}
                className="management-card"
                style={{
                  background: 'var(--bg-primary)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid var(--border-light)',
                  transition: 'all 0.3s ease'
                }}
              >
                {/* Member Image */}
                <div style={{ position: 'relative' }}>
                  <img 
                    src={member.image} 
                    alt={member.name}
                    style={{ 
                      width: '100%', 
                      height: '280px', 
                      objectFit: 'cover',
                      objectPosition: 'top'
                    }}
                  />
                  <div 
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                      padding: '40px 20px 16px 20px'
                    }}
                  >
                    <h3 className="heading-2" style={{ color: 'white', marginBottom: '4px' }}>
                      {member.name}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--brand-teal)', fontWeight: '600' }}>
                      {member.title}
                    </p>
                  </div>
                </div>

                {/* Member Details */}
                <div style={{ padding: '20px' }}>
                  <p className="body-small mb-4" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {member.bio}
                  </p>

                  {/* Expertise */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Briefcase size={14} style={{ color: 'var(--brand-orange)' }} />
                      <span className="body-small" style={{ color: 'var(--brand-orange)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10px' }}>
                        Expertise
                      </span>
                    </div>
                    <p className="body-small" style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                      {member.expertise}
                    </p>
                  </div>

                  {/* Certifications */}
                  {member.certifications && member.certifications.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Award size={14} style={{ color: 'var(--brand-teal)' }} />
                        <span className="body-small" style={{ color: 'var(--brand-teal)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10px' }}>
                          Certifications
                        </span>
                      </div>
                      <ul style={{ margin: 0, paddingLeft: '14px' }}>
                        {member.certifications.map((cert, idx) => (
                          <li key={idx} className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '2px', fontSize: '11px' }}>
                            {cert}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container text-center">
          <h2 className="hero-medium mb-6">Partner With Excellence</h2>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto 32px auto' }}>
            Let&apos;s discuss how Pinnacle Biomed can deliver your healthcare infrastructure project with unmatched quality and compliance.
          </p>
          <Link to="/contact" className="btn-primary">
            Request Project Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
